import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import jobsData from '../data/jobsData';
import { slugify } from '../utils/slugify';

const JobDetails = () => {
  const { slug } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      // Find job by matching the slug with the generated slug from title
      const selectedJob = jobsData.find(job => slugify(job.title) === slug);
      
      if (selectedJob) {
        setJob(selectedJob);
        document.title = `${selectedJob.title} - ${selectedJob.company} | JobsDriver`;
      } else {
        // Handle case where job is not found
        console.log(`Job not found for slug: ${slug}`);
        document.title = "Job Not Found | JobsDriver";
      }
    } catch (error) {
      console.error('Error finding job:', error);
      document.title = "Error Loading Job | JobsDriver";
    }
    
    setLoading(false);
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading job details...</p>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Job Not Found</h1>
          <p className="text-gray-600 mb-8">The job you're looking for doesn't exist or may have been removed.</p>
          <Link to="/jobs" className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700">
            Browse All Jobs
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Page Header */}
      <section className="relative py-20 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1920&q=80')" }}>
        {/* Overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Job Details</h1>
          <div className="flex items-center space-x-2 text-white">
            <Link to="/" className="hover:text-primary-300 transition-colors">Home</Link>
            <i className="fas fa-chevron-right text-sm"></i>
            <Link to="/jobs" className="hover:text-primary-300 transition-colors">Jobs</Link>
            <i className="fas fa-chevron-right text-sm"></i>
            <span className="text-primary-300">{job.title}</span>
          </div>
        </div>
      </section>

      {/* Job Details Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Job Header */}
              <div className="bg-white rounded-lg shadow-lg p-6 mb-8 border border-gray-200">
                <div className="flex flex-col md:flex-row md:items-start gap-6">
                  <div className="flex-shrink-0">
                    <img src={`https://placehold.co/100x100?text=${job.company.charAt(0)}`} alt={`${job.company} Logo`} className="w-20 h-20 rounded-lg object-cover bg-primary-100 flex items-center justify-center text-primary-600 font-bold" />
                  </div>
                  <div className="flex-grow">
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{job.title}</h2>
                    <div className="flex items-center text-gray-600 mb-4">
                      <i className="fas fa-building mr-2"></i>
                      <span className="font-medium">{job.company}</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="flex items-center text-gray-600">
                        <i className="fas fa-map-marker-alt mr-2 text-primary-500"></i>
                        <span>{job.location}</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <i className="fas fa-money-bill-wave mr-2 text-primary-500"></i>
                        <span>{job.salary}</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <i className="fas fa-clock mr-2 text-primary-500"></i>
                        <span>Posted {job.posted}</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <i className="fas fa-briefcase mr-2 text-primary-500"></i>
                        <span>{job.type}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row lg:flex-col gap-3">
                    <button className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200">Apply Now</button>
                    <button className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors duration-200">
                      <i className="far fa-bookmark mr-2"></i> Save Job
                    </button>
                    <button className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors duration-200">
                      <i className="fas fa-share-alt mr-2"></i> Share
                    </button>
                  </div>
                </div>
              </div>

              {/* Ad Banner 1 */}
              <div className="bg-white rounded-lg shadow-lg p-6 mb-8 border border-gray-200 text-center">
                <iframe 
                  border="0" 
                  src="https://www.trip.com/partners/ad/SB5794672?Allianceid=6648580&SID=228113005&trip_sub1=" 
                  style={{width:'300px', height:'250px'}} 
                  frameBorder="0" 
                  scrolling="no" 
                  title="Trip.com Advertisement"
                ></iframe>
              </div>

              {/* Job Description */}
              <div className="space-y-8">
                <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Job Description</h3>
                  <div className="prose prose-gray max-w-none">
                    <p className="text-gray-700 leading-relaxed">{job.description}</p>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Responsibilities</h3>
                  <ul className="space-y-3">
                    {job.responsibilities.map((responsibility, index) => (
                      <li key={index} className="flex items-start">
                        <i className="fas fa-check-circle text-primary-500 mt-1 mr-3 flex-shrink-0"></i>
                        <span className="text-gray-700">{responsibility}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Requirements</h3>
                  <ul className="space-y-3">
                    {job.requirements.map((requirement, index) => (
                      <li key={index} className="flex items-start">
                        <i className="fas fa-check-circle text-primary-500 mt-1 mr-3 flex-shrink-0"></i>
                        <span className="text-gray-700">{requirement}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Benefits</h3>
                  <ul className="space-y-3">
                    {job.benefits.map((benefit, index) => (
                      <li key={index} className="flex items-start">
                        <i className="fas fa-gift text-primary-500 mt-1 mr-3 flex-shrink-0"></i>
                        <span className="text-gray-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">How to Apply</h3>
                  <p className="text-gray-700 leading-relaxed mb-6">Please submit your resume and a cover letter explaining why you're a good fit for this position. Include links to your GitHub profile or portfolio if available.</p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <button className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200">Apply Now</button>
                    <button className="border border-primary-600 text-primary-600 hover:bg-primary-50 px-8 py-3 rounded-lg font-semibold transition-colors duration-200">Email This Job</button>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              {/* Company Info */}
              <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Company Information</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <i className="fas fa-globe text-primary-500 mt-1 flex-shrink-0"></i>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Website</h4>
                      <a href={job.companyInfo.website} target="_blank" rel="noreferrer" className="text-primary-600 hover:text-primary-700 transition-colors">{job.companyInfo.website.replace("https://", "")}</a>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <i className="fas fa-phone-alt text-primary-500 mt-1 flex-shrink-0"></i>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Phone</h4>
                      <a href={`tel:${job.companyInfo.phone}`} className="text-primary-600 hover:text-primary-700 transition-colors">{job.companyInfo.phone}</a>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <i className="fas fa-envelope text-primary-500 mt-1 flex-shrink-0"></i>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Email</h4>
                      <a href={`mailto:${job.companyInfo.email}`} className="text-primary-600 hover:text-primary-700 transition-colors">{job.companyInfo.email}</a>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <i className="fas fa-map-marker-alt text-primary-500 mt-1 flex-shrink-0"></i>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Location</h4>
                      <p className="text-gray-700">{job.companyInfo.location}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <i className="fas fa-users text-primary-500 mt-1 flex-shrink-0"></i>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Company Size</h4>
                      <p className="text-gray-700">{job.companyInfo.size}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <i className="fas fa-briefcase text-primary-500 mt-1 flex-shrink-0"></i>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Industry</h4>
                      <p className="text-gray-700">{job.companyInfo.industry}</p>
                    </div>
                  </div>
                </div>
                <Link to="/organizations" className="block w-full mt-6 border border-primary-600 text-primary-600 hover:bg-primary-50 px-6 py-3 rounded-lg font-semibold text-center transition-colors duration-200">View Company Profile</Link>
              </div>

              {/* Job Overview */}
              <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Job Overview</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <i className="fas fa-calendar-alt text-primary-500 mt-1 flex-shrink-0"></i>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Date Posted</h4>
                      <p className="text-gray-700">{job.datePosted}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <i className="fas fa-hourglass-end text-primary-500 mt-1 flex-shrink-0"></i>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Expiration Date</h4>
                      <p className="text-gray-700">{job.expirationDate}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <i className="fas fa-map-marker-alt text-primary-500 mt-1 flex-shrink-0"></i>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Location</h4>
                      <p className="text-gray-700">{job.location}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <i className="fas fa-briefcase text-primary-500 mt-1 flex-shrink-0"></i>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Job Type</h4>
                      <p className="text-gray-700">{job.type}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <i className="fas fa-money-bill-wave text-primary-500 mt-1 flex-shrink-0"></i>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Salary</h4>
                      <p className="text-gray-700">{job.salary}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <i className="fas fa-graduation-cap text-primary-500 mt-1 flex-shrink-0"></i>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Education</h4>
                      <p className="text-gray-700">{job.education}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <i className="fas fa-user-clock text-primary-500 mt-1 flex-shrink-0"></i>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Experience</h4>
                      <p className="text-gray-700">{job.experience}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <i className="fas fa-venus-mars text-primary-500 mt-1 flex-shrink-0"></i>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Gender</h4>
                      <p className="text-gray-700">{job.gender}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Ad Banner 2 */}
              <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200 text-center">
                <iframe 
                  border="0" 
                  src="https://www.trip.com/partners/ad/SB5794672?Allianceid=6648580&SID=228113005&trip_sub1=" 
                  style={{width:'300px', height:'250px'}} 
                  frameBorder="0" 
                  scrolling="no" 
                  title="Trip.com Advertisement"
                ></iframe>
              </div>

              {/* Similar Jobs */}
              <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Similar Jobs</h3>
                <div className="space-y-4">
                  {jobsData
                    .filter(item => item.id !== job.id && item.company !== job.company) // Filter out current job and jobs from same company
                    .slice(0, 3) // Show only first 3 similar jobs
                    .map((similarJob) => (
                      <div key={similarJob.id} className="flex items-start space-x-3 p-4 border border-gray-200 rounded-lg hover:border-primary-300 transition-colors cursor-pointer" onClick={() => {
                        // Convert job title to slug
                        const slug = slugify(similarJob.title);
                        window.location.href = `/job-details/${slug}`;
                      }}>
                        <div className="flex-shrink-0">
                          <img src={`https://placehold.co/50x50?text=${similarJob.company.charAt(0)}`} alt={`${similarJob.company} Logo`} className="w-12 h-12 rounded-lg object-cover bg-primary-100 flex items-center justify-center text-primary-600 font-bold text-sm" />
                        </div>
                        <div className="flex-grow min-w-0">
                          <h4 className="font-semibold text-gray-900 mb-1">
                            <Link to={`/job-details/${slugify(similarJob.title)}`} className="hover:text-primary-600 transition-colors">{similarJob.title}</Link>
                          </h4>
                          <div className="text-sm text-gray-600 mb-2">{similarJob.company}</div>
                          <div className="flex items-center text-sm text-gray-500 mb-1">
                            <i className="fas fa-map-marker-alt mr-1"></i> {similarJob.location}
                          </div>
                          <div className="flex items-center text-sm text-gray-500">
                            <i className="fas fa-money-bill-wave mr-1"></i> {similarJob.salary}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Job Alert Section */}
      <section className="bg-primary-600 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-center lg:text-left">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Get Job Alerts in Your Email</h2>
              <p className="text-xl text-primary-100">Stay updated with the latest job opportunities matching your preferences</p>
            </div>
            <form className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto lg:min-w-96">
              <input type="email" placeholder="Your email address" required className="flex-grow px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none" />
              <button type="submit" className="bg-white text-primary-600 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors duration-200 whitespace-nowrap">Subscribe</button>
            </form>
          </div>
        </div>
      </section>
    </>
  );
};

export default JobDetails;